import * as React from "react";
import { ApolloClient } from "@apollo/client";
import { withApollo } from "@apollo/client/react/hoc";
import { RouteComponentProps } from "react-router-dom";
import moment from "moment";
import _, { flowRight as compose } from "lodash";

import downloadIcon from "assets/download.svg";

import Icon from "components/Icon";
import { getProgramIdFromSearch } from "utilities/routes";
import Clickable from "components/Clickable";
import variables from "styles/variables";
import getServerRootUrl from "utilities/getServerRootUrl";
import { withUserInfoNoCache, UserInfoQuery } from "modules/Dashboard/UserInfo";

import styles from "./styles.scss";

import dashboardPdfUrl from "./dashboardPdfUrl.gql";
import dashboardQuery from "./dashboard.gql";

import DashboardPlaceholder from "../DashboardPlaceholder";
import ImageQuestionGallery, { ReportDashboardImageQuestion } from "./ImageQuestionGallery";
import { ProgramsRouteParams } from "../../index";
import { withDownloads, DownloadsContext } from "../../../../Downloads";
import { ParsedViewport, parseViewport, Viewport } from "./viewport";

export interface IReportDashboard {
  key: string;
  title: string;
  url: string | null;
  viewport: ParsedViewport;
  downloadPdf: boolean;
  imageQuestions: ReportDashboardImageQuestion[];
}

interface ReportDashboardRouteParams extends ProgramsRouteParams {
  dashboard: string;
}

interface Props extends RouteComponentProps<ReportDashboardRouteParams>, UserInfoQuery {
  client: ApolloClient<any>;
  downloads: DownloadsContext;
  viewport: Viewport;
  onViewportChange: (viewport: Viewport) => void;
  onDashboardUnavailable: (key: string | null) => void;
}

interface State {
  loadingIframe: boolean;
  dashboard: IReportDashboard | null;
  waiting: boolean;
}

class ReportDashboard extends React.Component<Props, State> {
  readonly state: State = {
    loadingIframe: true,
    dashboard: null,
    waiting: false,
  };

  public iframeRef = React.createRef<HTMLIFrameElement>();

  public componentDidMount() {
    window.addEventListener("message", this.handleOnMessage);
    this.loadDashboard();
  }

  public componentWillUnmount() {
    window.removeEventListener("message", this.handleOnMessage);
  }

  public componentDidUpdate({ match: { params: oldParams }, location: oldLocation, viewport: oldViewport }: Props) {
    const {
      match: { params },
      location,
      viewport,
    } = this.props;
    const oldId = getProgramIdFromSearch(oldLocation.search);
    const newId = getProgramIdFromSearch(location.search);
    const viewportS = JSON.stringify(viewport);
    const oldViewportS = JSON.stringify(oldViewport);

    if (oldParams.dashboard !== params.dashboard || oldId !== newId || viewportS !== oldViewportS) {
      this.setState({ dashboard: null, loadingIframe: true, waiting: false });
      this.loadDashboard();
    }
  }

  public async loadDashboard() {
    const {
      client,
      match: {
        params: { dashboard: dashboardKey },
      },
      viewport,
    } = this.props;
    const id = getProgramIdFromSearch(this.props.location.search);

    try {
      const {
        data: { reportDashboard: dashboard },
      } = await client.query({
        query: dashboardQuery,
        variables: {
          dashboardKey,
          programIds: !id || id === "all" ? null : id.split(","),
          viewport: parseViewport(viewport),
        },
      });

      this.setState({ dashboard });
    } catch (error) {
      if (_.get(error, "graphQLErrors.0.message") === "dashboardNotFound") {
        this.props.onDashboardUnavailable(dashboardKey);
      }
    }
  }
  public handleOnMessage = (e: MessageEvent) => {
    const { current: iframe } = this.iframeRef;

    if (!iframe) return;

    if (e.source === iframe.contentWindow) {
      const edata = e.data as any;

      if (edata.event_type === "dashboard_resize") {
        iframe.style.height = edata.dashboard_height + 50 + "px";
        this.setState({ loadingIframe: false });
      } else if (edata.event_type === "viewport_changed") {
        const viewport = _.pick(edata, ["aggregation", "daterange", "filters"]);
        this.props.onViewportChange(viewport);
      }
    }
  };

  public renderImages() {
    const { dashboard, loadingIframe } = this.state;
    return dashboard ? (
      <div className={styles.images} hidden={loadingIframe}>
        {dashboard.imageQuestions.map(q => (
          <ImageQuestionGallery key={q.id} question={q} />
        ))}
      </div>
    ) : null;
  }

  public downloadPDF = async () => {
    const { waiting, dashboard } = this.state;
    if (waiting || !dashboard) return;

    const {
      match: {
        params: { dashboard: dashboardKey },
      },
      location,
      downloads,
      client,
      viewport,
    } = this.props;
    const id = getProgramIdFromSearch(location.search);

    this.setState({ waiting: true }, () => {
      setTimeout(() => {
        this.setState({ waiting: false });
      }, 3000);
    });

    const fileId = downloads.createFile(`${dashboard.title}-${moment().format("YYYYMMDDHHmmss")}.pdf`, {
      withAuth: true,
    });

    const { data } = await client.mutate({
      mutation: dashboardPdfUrl,
      variables: {
        dashboardKey,
        programIds: !id || id === "all" ? null : id.split(","),
        viewport: parseViewport(viewport),
      },
    });

    if (data) {
      const pdfUrl = getServerRootUrl() + "/dashboard-pdf/" + data.createReportDashboardPdf;

      downloads.setFileUrl({
        id: fileId,
        url: pdfUrl,
        pollUrl: pdfUrl,
        method: { method: "until-body-type", type: "binary/octet-stream" },
      });
    }
  };

  public render() {
    const { dashboard, loadingIframe, waiting } = this.state;

    return (
      <div className={styles.rdashboard}>
        {(loadingIframe || !dashboard) && <DashboardPlaceholder />}

        {dashboard && dashboard.url && (
          <React.Fragment>
            {dashboard.downloadPdf && (
              <Clickable
                actionLabel={""}
                onClick={this.downloadPDF}
                className={waiting ? styles.waitingDownloadBtn : styles.downloadBtn}
              >
                <span className={styles.downloadText}>Download PDF</span>
                <Icon src={downloadIcon} size={20} fill={variables.gray1} />
              </Clickable>
            )}

            <iframe
              ref={this.iframeRef}
              title={"Report Dashboard"}
              src={dashboard.url}
              className={loadingIframe ? styles.loadingIframe : styles.iframe}
            />
          </React.Fragment>
        )}

        {this.renderImages()}
      </div>
    );
  }
}

export default compose(withApollo, withUserInfoNoCache, withDownloads)(ReportDashboard);
